import { useEffect, useState } from 'react';
import { articoli } from '../../api/articoli';
import type {
  AbbinamentoLetto,
  ConfigurazioneIa,
  EsitoRicercaAbbinamenti,
} from '../../api/articoli';
import { formattaImporto, formattaPercentuale } from '../../lib/formato';

/*  Il cancello: due articoli affiancati, e qualcuno che dice se sono lo stesso.

    Il confronto prezzi vive su una chiave sola — sigla piu' codice del produttore — e
    regge finche' tutti scrivono lo stesso codice. I grossisti non lo fanno: Abbattista
    chiama «01376#031» quello che Gessi chiama «01376 031», e su seicento articoli Gessi
    del suo catalogo il confronto ne accoppiava zero.

    Qui si guarda quello che qualcuno ha proposto e si dice si' o no. E' il pezzo piu'
    importante di tutta la catena, e non perche' sia complicato: perche' e' l'unico punto
    in cui una persona guarda. Abbinare due articoli diversi vuol dire mettere il prezzo
    dell'uno sull'altro — il numero finisce in un preventivo, e' plausibile, e si scopre
    dal cliente.

    QUINDI LE PROVE STANNO TUTTE QUI, E I DUE COSTI IN MEZZO

    Due articoli che dicono di essere lo stesso e costano uno il triplo dell'altro sono
    quasi sempre due articoli diversi. E' la cosa che si guarda per prima, quindi sta in
    mezzo alle due colonne e non in fondo: la differenza in percentuale si legge senza
    sottrarre a mente.

    Non c'e' un «conferma tutte». Sarebbe la cosa piu' richiesta e la piu' dannosa: una
    coda che si svuota con un tasto smette di essere un cancello e diventa un timbro, e a
    quel punto tutta l'impalcatura — proposta, prove, conferma — non protegge piu' da
    niente. Quindici proposte si guardano in due minuti; se un giorno fossero mille, la
    risposta non e' un tasto solo, e' una regola migliore che ne proponga meno.
*/
export function Abbinamenti({
  decidibili,
  onChiuso,
}: {
  /** Falso per chi puo' solo guardare: i due tasti non compaiono. */
  decidibili: boolean;
  onChiuso: () => void;
}) {
  const [coda, setCoda] = useState<AbbinamentoLetto[] | null>(null);
  const [ia, setIa] = useState<ConfigurazioneIa | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [sigla, setSigla] = useState('');
  const [esito, setEsito] = useState<EsitoRicercaAbbinamenti | null>(null);

  const ricarica = () => {
    articoli.abbinamenti
      .inAttesa()
      .then(setCoda)
      .catch((e: Error) => setErrore(e.message));
  };

  useEffect(() => {
    ricarica();

    /*  Lo stato del modello si legge una volta all'apertura: dice se la ricerca puo'
        andare oltre la regola sui codici, e senza chiave e' meglio scriverlo qui che
        lasciarlo scoprire premendo.  */
    articoli
      .configurazioneIa()
      .then(setIa)
      .catch(() => setIa(null));
  }, []);

  const cerca = (evento: React.FormEvent) => {
    evento.preventDefault();
    setInCorso('ricerca');
    setErrore(null);
    setEsito(null);

    articoli.abbinamenti
      .cerca(sigla.trim().toUpperCase())
      .then((risposta) => {
        setEsito(risposta);
        ricarica();
      })
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(null));
  };

  const decidi = (abbinamento: AbbinamentoLetto, si: boolean) => {
    setInCorso(abbinamento.id);
    setErrore(null);

    const azione = si
      ? articoli.abbinamenti.conferma(abbinamento.id)
      : articoli.abbinamenti.rifiuta(abbinamento.id);

    azione
      /*  Si toglie dalla coda invece di ricaricarla tutta: chi sta guardando la terza
          proposta di quindici non deve ritrovarsi in cima a ogni tasto premuto.  */
      .then(() => setCoda((righe) => (righe ?? []).filter((r) => r.id !== abbinamento.id)))
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(null));
  };

  return (
    <aside className="scheda abbinamenti">
      <header>
        <div>
          <h2>Abbinamenti</h2>
          <p className="dettaglio">Articoli che sarebbero lo stesso, con codici diversi</p>
        </div>
        <button type="button" className="chiudi" onClick={onChiuso} aria-label="Chiudi">
          ×
        </button>
      </header>

      {errore !== null && <p className="errore">{errore}</p>}

      {decidibili && (
        <form className="blocco cerca-abbinamenti" onSubmit={cerca}>
          <label>
            Cerca per marca
            <input
              value={sigla}
              onChange={(e) => setSigla(e.target.value.toUpperCase())}
              maxLength={3}
              minLength={3}
              placeholder="GES"
              required
            />
          </label>

          <button type="submit" className="primario" disabled={inCorso !== null}>
            {inCorso === 'ricerca' ? 'Cerco…' : 'Cerca'}
          </button>

          <p className="nota">
            La sigla Metel di tre caratteri. Si cerca una marca per volta perché è
            l'unità in cui si ragiona guardandole — e perché quando al posto della regola
            ci sarà un modello, chiedergli di tutto il catalogo insieme sarebbe centomila
            articoli in una domanda sola.
          </p>

          {/*  Cosa può fare il modello qui. Senza chiave la ricerca funziona lo stesso —
               la regola sui codici non chiede niente a nessuno — ma trova solo quello che
               una regola può trovare, e vale la pena saperlo prima di concludere che non
               c'è altro da abbinare.  */}
          {ia !== null && !ia.testoDisponibile && (
            <p className="nota">
              Il modello non è configurato: si cerca con la sola regola sui codici, che
              prende quelli uguali a meno dei separatori. {ia.motivo}
            </p>
          )}

          {ia?.testoDisponibile && (
            <p className="nota">
              Modello <strong>{ia.modelloTesto}</strong> (chiave {ia.chiaveMascherata}), al
              massimo {ia.richiesteMassimePerLavoro} richieste per ricerca.
            </p>
          )}
        </form>
      )}

      {esito !== null && (
        <p className="esito-ricerca">
          {esito.articoliGuardati} articoli di <strong>{esito.siglaProduttore}</strong>{' '}
          guardati da «{esito.proponente}»:{' '}
          {esito.proposte === 0
            ? 'nessuna proposta nuova'
            : esito.proposte === 1
              ? 'una proposta nuova'
              : `${esito.proposte} proposte nuove`}
          {esito.gia > 0 && `, ${esito.gia} già viste`}.
          {esito.messaggio !== null && <> {esito.messaggio}</>}
        </p>
      )}

      <section className="blocco">
        <h3>
          {coda === null
            ? 'Da guardare'
            : coda.length === 0
              ? 'Niente da guardare'
              : coda.length === 1
                ? 'Una da guardare'
                : `${coda.length} da guardare`}
        </h3>

        {coda === null && <p className="attesa">Lettura…</p>}

        {coda !== null && coda.length === 0 && (
          <p className="nota">
            La coda è vuota. Si riempie cercando su una marca: quello che entra qui non
            vale niente finché qualcuno non lo guarda.
          </p>
        )}

        {coda?.map((a) => (
          <Proposta
            key={a.id}
            abbinamento={a}
            decidibile={decidibili}
            inCorso={inCorso === a.id}
            onDecisa={(si) => decidi(a, si)}
          />
        ))}
      </section>
    </aside>
  );
}

/*  Una proposta: i due articoli, i due costi in mezzo, e il perché sotto.

    L'ordine non è casuale. Prima i due codici, che sono la domanda; poi i due costi con la
    differenza, che è il controllo che smaschera gli abbinamenti sbagliati; poi le
    descrizioni, che sono la conferma; e in fondo la motivazione di chi ha proposto, che si
    legge quando le prime tre non bastano.
*/
function Proposta({
  abbinamento: a,
  decidibile,
  inCorso,
  onDecisa,
}: {
  abbinamento: AbbinamentoLetto;
  decidibile: boolean;
  inCorso: boolean;
  onDecisa: (si: boolean) => void;
}) {
  /*  Quanto costa di più il caro rispetto all'economico. È il numero che fa fermare: una
      differenza del dieci per cento fra due distributori è normale, una del trecento vuol
      dire quasi sempre due articoli diversi.  */
  const divario =
    a.costoVariante !== null && a.costoCanonico !== null && a.costoVariante > 0 && a.costoCanonico > 0
      ? Math.abs(a.costoVariante - a.costoCanonico) / Math.min(a.costoVariante, a.costoCanonico) * 100
      : null;

  return (
    <article className={`proposta${divario !== null && divario > 200 ? ' divario-largo' : ''}`}>
      <div className="due-articoli">
        <div className="articolo">
          <span className="chiave">
            {a.siglaProduttore} {a.codiceCanonico}
          </span>
          <span className="fornitore">{a.fornitoreCanonico}</span>
          <span className="descrizione">{a.descrizioneCanonico}</span>
          <strong className="costo">
            {a.costoCanonico === null ? '—' : formattaImporto(a.costoCanonico)}
          </strong>
        </div>

        <div className="segno" aria-hidden="true">
          =?
        </div>

        <div className="articolo">
          <span className="chiave">
            {a.siglaProduttore} {a.codiceVariante}
          </span>
          <span className="fornitore">{a.fornitoreVariante}</span>
          <span className="descrizione">{a.descrizioneVariante}</span>
          <strong className="costo">
            {a.costoVariante === null ? '—' : formattaImporto(a.costoVariante)}
          </strong>
        </div>
      </div>

      {divario !== null && (
        <p className={`divario${divario > 200 ? ' allarme' : ''}`}>
          {divario < 1
            ? 'Costano quasi uguale.'
            : `Uno costa il ${formattaPercentuale(divario)} in più dell'altro.`}
          {divario > 200 && ' Una differenza così è quasi sempre due articoli diversi: guarda bene.'}
        </p>
      )}

      <details className="perche">
        <summary>
          Proposto da «{a.proponente}», sicurezza {formattaPercentuale(a.confidenza * 100)}
        </summary>
        <p>{a.motivazione}</p>
      </details>

      {decidibile && (
        <div className="comandi">
          <button
            type="button"
            className="primario"
            disabled={inCorso}
            onClick={() => onDecisa(true)}
          >
            {inCorso ? 'Attendi…' : 'È lo stesso'}
          </button>
          <button type="button" disabled={inCorso} onClick={() => onDecisa(false)}>
            Non lo è
          </button>
        </div>
      )}
    </article>
  );
}
